import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import checkIsValidEmail from 'utils/checkIsValidEmail';

import { setModal } from 'modules/_shared/redux/modalActions';
import { notifyDividendToPartners } from 'modules/communications/api/notifyPartnersDividend';
import { addConfigurationToEmailData } from 'utils/emails';
import { sendTestEmail } from 'modules/communications/api/sendTestEmail';
import { useGetPartnersData } from '../hooks/useGetPartnersData';

import { notifyPartnersDividendsModalInitValue } from '../constants/notifyPartnersDividendsModalInitValue';

import { NotifyPartnersDividendsModalContextType } from '../types/NotifyPartnersDividendsModalContextType';

import { getEmailData } from '../utils/getEmailData';
import {
  getAxaDividendBounceAllowedEmails,
  isAxaDividendBounceRetryActive,
} from '../utils/isAxaDividendBounceRetryActive';

const NotifyPartnersDividendsModalContext =
  createContext<NotifyPartnersDividendsModalContextType>(
    notifyPartnersDividendsModalInitValue
  );

export const NotifyPartnersDividendsModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  operationId: string | undefined;
}> = ({ children, operationId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [expandView, setExpandView] = useState<boolean>(true);
  const [expandDirection, setExpandDirection] = useState<string>('up');
  const [showTestEmailPanel, setShowTestEmailPanel] = useState<boolean>(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState<string>('');
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: dividendPartnersData, isLoading: isLoadingPartners } =
    useGetPartnersData({
      operationId,
      page: currentPage,
    });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeView = () => {
    const newView = !expandView;
    const newDirection = newView ? 'up' : 'down';
    setExpandView(newView);
    setExpandDirection(newDirection);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleToggleTestEmailPanel = () => {
    setShowTestEmailPanel((prev) => !prev);
    setTestEmailRecipient('');
    setInvalidEmail(false);
  };

  const handleChangeTestEmailRecipient = (email: string) => {
    const validEmail: boolean = checkIsValidEmail(email);

    setInvalidEmail(!validEmail);
    setTestEmailRecipient(email);
  };

  const handleSendEmails = async () => {
    try {
      setIsLoading(true);
      if (!operationId) {
        return;
      }
      const emailData = getEmailData({
        subject: `${t('PartnersDividends')}-${actualSociety?.name}`,
        actualSociety,
        userId: user?._id,
        operationId,
      });

      const emailDataWithConfiguration = actualSociety
        ? addConfigurationToEmailData(actualSociety, emailData)
        : emailData;

      await store.dispatch(
        notifyDividendToPartners({
          operationId,
          societyId: actualSociety?._id,
          emailData: emailDataWithConfiguration,
        })
      );

      successSwal(t('NotificationSentSuccessfully'));
      handleCloseModal();
    } catch (error) {
      errorSwal(t('ErrorSendingNotification'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setIsLoading(true);

      if (!operationId) {
        return;
      }

      const DEFAULT_RECIPIENT = 'Destinatario';

      const emailData = getEmailData({
        subject: `${t('PartnersDividends')}-${actualSociety?.name}`,
        actualSociety,
        userId: user?._id,
        operationId,
      });

      const emailDataWithConfiguration = actualSociety
        ? addConfigurationToEmailData(actualSociety, emailData)
        : emailData;

      const testEmailData = {
        ...emailDataWithConfiguration,
        to: [{ email: testEmailRecipient?.trim(), name: DEFAULT_RECIPIENT }],
      };

      await sendTestEmail({ emailData: testEmailData });
      successSwal(t('TestEmailSentSuccessfully'));
      setIsLoading(false);
    } catch (error) {
      errorSwal(t('ErrorSendingTestEmail'));
      setIsLoading(false);
    }
  };

  const showBounceRetryWarning = isAxaDividendBounceRetryActive(operationId);
  const bounceRetryAllowedEmails = showBounceRetryWarning
    ? getAxaDividendBounceAllowedEmails()
    : [];

  return (
    <NotifyPartnersDividendsModalContext.Provider
      value={{
        data: {
          partnersData: dividendPartnersData.partnersData,
          totalPartners: dividendPartnersData.totalPartners,
          expandView,
          expandDirection,
          invalidEmailsAlert: dividendPartnersData.invalidEmailsAlert,
          hasInvalidEmails: dividendPartnersData.hasInvalidEmails,
          hasGeneratedEmails: dividendPartnersData.hasGeneratedEmails,
          generatedEmailsAlert: dividendPartnersData.generatedEmailsAlert,
          showTestEmailPanel,
          testEmailRecipient,
          invalidEmail,
          currentPage,
          rowsPerPage,
          isLoading: isLoading || isLoadingPartners,
          showBounceRetryWarning,
          bounceRetryAllowedEmails,
        },
        actions: {
          handleChangeView,
          handleCloseModal,
          handleSendEmails,
          handleSendTestEmail,
          handleToggleTestEmailPanel,
          handleChangeTestEmailRecipient,
          handleChangePage,
          handleChangeRowsPerPage,
        },
      }}
    >
      {children}
    </NotifyPartnersDividendsModalContext.Provider>
  );
};

export const useNotifyPartnersDividendsModal = () =>
  useContext(NotifyPartnersDividendsModalContext);
